<template>
  <section
    v-if="!isRetailerLoading"
    :key="updateKey"
    class="grid gap-8 md:gap-6 pb-3"
    :class="{'md:grid-cols-2 pt-1': !isAccountSection}"
    aria-labelledby="pickup-info"
  >
    <div>
      <pdl-label v-if="!isAccountSection" qaid="selected-retailer-store-pickup-label" kind="default" class="mb-1">
        {{ $t('checkout.multi.pickupInStore.location') }}
      </pdl-label>

      <template v-if="!Object.keys(displayRetailer).length">
        <div qaid="selected-retailer-no-retailer-message" class="font-bold mb-1" role="alert" aria-live="polite">
          {{ noSelectedRetailerMessage }}
        </div>
      </template>
      <template v-else>
        <div>
          <div qaid="selected-retailer-name" class="font-bold flex items-center gap-1 justify-left">
            <p id="pickup-info">{{ displayRetailer.displayName }}</p>
            <span
              v-if="displayRetailer.formattedDistance"
              qaid="selected-retailer-formatted-distance"
              class="font-normal text-gray-60"
            >
              {{ `(${displayRetailer.formattedDistance} ${unitAbbreviation})` }}
            </span>
          </div>
          <address-summary
            v-if="displayRetailer.address"
            custom-classes="mb-1"
            :address="displayRetailer.address"
            qaid="selected-retailer-store-pickup-address"
          />
        </div>
      </template>
      <pdl-button
        text
        small
        underline
        aria-haspopup="true"
        :aria-expanded="isRetailerDrawerOpen"
        qaid="selected-retailer-store-pickup-edit"
        class="py-1"
        @click="handleRetailerClick"
      >
        {{ $t('basket.page.shipping.change.store') }}
      </pdl-button>
    </div>
    <div v-if="!isAccountSection">
      <pdl-label qaid="selected-retailer-postcode-label" kind="default" class="mb-1">
        {{ $t('checkout.summary.deliveryAddress') }}
      </pdl-label>
      <address-summary
        v-if="hasDrawerDeliveryAddress"
        custom-classes="mb-1"
        :address="drawerDeliveryAddress"
        qaid="selected-retailer-delivery-address"
      />
      <div v-else>
        <span
          class="mb-1 text-orange-100 bg-orange-10 inline-flex gap-x-1 items-center px-1"
          role="alert"
          aria-live="polite">
          <pdl-icon name="error" size="18"></pdl-icon>
          <span class="text-orange-140">{{ errorMessage }}</span>
        </span>
      </div>
      <div
        v-if="selectedPostcode && !hasDrawerDeliveryAddress"
        class="mb-1"
        qaid="selected-retailer-ship-to-postal-code"
      >
        {{ selectedPostcode }}
      </div>

      <pdl-button
        text
        underline
        aria-haspopup="true"
        :aria-expanded="isPostalCodeDrawerOpen"
        qaid="selected-retailer-delivery-address-edit"
        class="py-1"
        @click="handlePostcodeClick"
      >
        {{ $t('text.edit') }}
      </pdl-button>
    </div>
    <postal-code-drawer :is-drawer-open="isPostalCodeDrawerOpen" @close="isPostalCodeDrawerOpen = false" />
    <retailer-drawer
      :is-drawer-open="isRetailerDrawerOpen"
      :is-account-section="isAccountSection"
      @close="isRetailerDrawerOpen = false"
    />
  </section>
  <div v-else class="h-20" aria-busy="true" :aria-label="$t('aria.pickupinstore.loading')">
    <pdl-loading is-loading />
  </div>
</template>

<script>
import {mapState, mapGetters, mapActions} from 'vuex';
import {PdlLabel} from '@pedal/pdl-label';
import {PdlButton} from '@pedal/pdl-button';
import {PdlLoading} from '@pedal/pdl-loading';
import {PdlIcon} from '@pedal/pdl-icon';
import AddressSummary from '@/components/AddressSummary';
import RetailerDrawer from '@/components/drawer/retailer/RetailerDrawer.vue';
import PostalCodeDrawer from '@/components/drawer/address/PostalCodeDrawer.vue';
import {getFormattedAddress, populateAddress} from '@/utils/cart/delivery-address-util.js';
import {PdlToastType} from '@/constants/pdl-toast-type';

export default {
  name: 'SelectedRetailer',

  components: {
    PdlLabel,
    AddressSummary,
    PdlButton,
    RetailerDrawer,
    PostalCodeDrawer,
    PdlLoading,
    PdlIcon,
  },

  props: {
    isAccountSection: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      isRetailerDrawerOpen: false,
      isPostalCodeDrawerOpen: false,
    };
  },

  computed: {
    ...mapState('user', [
      'selectedRetailer',
      'userPreferredRetailer',
      'selectedLocation',
      'isRetailerLoading',
      'drawerDeliveryAddress',
    ]),
    ...mapState('checkout', ['billingAddress']),
    ...mapGetters('backend', ['unitAbbreviation', 'countryCode']),
    ...mapGetters('cart', ['cartEntriesQuantityMap']),
    selectedPostcode() {
      return this.selectedLocation?.postcode;
    },
    displayRetailer() {
      // In account section, show user preferred retailer
      if (this.isAccountSection) {
        return this.userPreferredRetailer && Object.keys(this.userPreferredRetailer).length
          ? this.userPreferredRetailer
          : {};
      }
      // In non-account sections, always show selected retailer
      return this.selectedRetailer;
    },
    updateKey() {
      return `${this.selectedPostcode}-${this.displayRetailer?.name}-${getFormattedAddress(
        this.drawerDeliveryAddress
      )}`;
    },
    hasDrawerDeliveryAddress() {
      return (
        Object.keys(this.drawerDeliveryAddress).length &&
        Object.values(this.drawerDeliveryAddress).some((value) => value !== null)
      );
    },
    errorMessage() {
      if (!this.hasDrawerDeliveryAddress) {
        return this.selectedPostcode
          ? this.$t('checkout.multi.shipToHome.address.detailsNeeded2')
          : this.$t('checkout.multi.shipToHome.address.detailsNeeded1');
      }
      return '';
    },
    noSelectedRetailerMessage() {
      return this.isAccountSection ? this.$t('profile.no.preferredRetailer') : this.$t('checkout.multi.retailer.error');
    },
  },
  watch: {
    async displayRetailer(newVal, oldVal) {
      if (newVal?.name === oldVal?.name) return;
      const response = await this.updateCartDealerAndCartEntries(this.displayRetailer.name);
      if (!response) return;
      this.showCartQuantityUpdateToast(response?.entries);
    },
    async selectedRetailer(newVal, oldVal) {
      // Keep this watcher for non-account sections
      if (this.isAccountSection) return; // Skip if account section, let displayRetailer handle it
      if (newVal?.name === oldVal?.name) return;
      const response = await this.updateCartDealerAndCartEntries(this.selectedRetailer.name);
      if (!response) return;
      this.showCartQuantityUpdateToast(response?.entries);
    },
    async selectedLocation(newVal, oldVal) {
      if (newVal?.postcode === oldVal?.postcode) return;
      this.updateCartAddress(await populateAddress(this.countryCode, this.selectedLocation, this.billingAddress));
    },
  },
  methods: {
    ...mapActions('cart', ['updateCartDealerAndCartEntries', 'updateCartAddress']),
    handlePostcodeClick() {
      this.isPostalCodeDrawerOpen = !this.isPostalCodeDrawerOpen;
    },
    handleRetailerClick() {
      this.isRetailerDrawerOpen = !this.isRetailerDrawerOpen;
    },
    showCartQuantityUpdateToast(updatedCartEntries) {
      const hasQuantityChanged = updatedCartEntries.some(({entryNumber, quantity}) => {
        return this.cartEntriesQuantityMap.get(entryNumber) !== quantity;
      });

      if (!hasQuantityChanged) return;

      this.$notify({
        type: PdlToastType.INFO,
        message: this.$t('basket.information.quantity.adjusted'),
        showClose: true,
      });
    },
  },
};
</script>
